import { useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  useGetCountryQuery,
  useGetCurrencyQuery,
  useGetSectorQuery,
  useGetTimezoneQuery
} from '@utils/api';
import { getFileNameFromUrl } from '@utils/common';
import { useGetOpportunitiesListQuery } from '@modules/opportunities/opportunities-list/api';
import { showGenericErrorToast } from '@utils/error';
import { CompanyProductType } from '@constants/common';

import {
  useGetCompanyConfigurationQuery,
  useLazyGetCompanyQuery
} from '../api';
import {
  CreateProductFormDefaultFieldValues,
  SectionMapper
} from '../constants';

const useCreateCompany = () => {
  const { companyId } = useParams();

  const [getCompany, { data: companyDetails, isLoading }] =
    useLazyGetCompanyQuery();

  const { data: countriesList } = useGetCountryQuery({ searchTerm: '' });
  const { data: currencyList } = useGetCurrencyQuery({ searchTerm: '' });
  const { data: timeZoneList } = useGetTimezoneQuery();
  const { data: sectors } = useGetSectorQuery();
  const { data: companyConfiguration } = useGetCompanyConfigurationQuery({
    companyId: companyId
  });

  const { data: opportunityResponse } = useGetOpportunitiesListQuery({});

  const opportunityList = opportunityResponse?.data ?? [];

  const getResetLegalInfoSection = (formValues: FieldValues) => ({
    ...formValues,
    companyRegisteredName: companyDetails?.registered_name || '',
    cin: companyDetails?.cin || '',
    registrationNumber: companyDetails?.registration_number || '',
    incorporationCountry: companyDetails?.incorporated_in || '',
    currency: companyDetails?.currency || ''
  });

  const getResetAddressSection = (formValues: FieldValues) => ({
    ...formValues,
    addressLine1: companyDetails?.address?.address_line_1 || null,
    addressLine2: companyDetails?.address?.address_line_2 || null,
    city: companyDetails?.address?.city || null,
    state: companyDetails?.address?.state || null,
    country: companyDetails?.address?.country || '',
    pin: companyDetails?.address?.pin
      ? String(companyDetails?.address?.pin)
      : null,
    timezone: companyDetails?.address?.time_zone || null
  });

  const getResetBrandSection = (formValues: FieldValues) => {
    const hexValue =
      companyConfiguration?.brand_color || companyDetails?.hex_value;

    return companyDetails
      ? {
          ...formValues,
          companyBrandName: companyDetails.company_known_name || '',
          website: companyDetails.website || null,
          companyLogo: {
            file: companyDetails.icon_url
              ? new File([], getFileNameFromUrl(companyDetails.icon_url))
              : undefined,
            path: companyDetails.icon_url || ''
          },
          companyStage: companyDetails.company_stage || '',
          sector:
            companyDetails.sector_and_category
              ?.map(item => item.name)
              .join(', ') || '',
          companyOneLiner: companyDetails.company_one_liner || '',
          detailedDescription: companyDetails.company_description || null,
          companyEmailDomains:
            companyDetails.email_domains?.map(val => val.domain_name) ?? [],
          hexValue: hexValue ? `#${hexValue.substring(4)}` : ''
        }
      : {};
  };

  const getProductAssets = (
    assets: {
      id: string;
      path: string;
      asset_type: string;
      fileName: string;
      order: number;
    }[]
  ) => {
    const productAsets = assets.map(val => ({
      id: val.id,
      file: val.path ? new File([], val.fileName) : undefined,
      path: val.path,
      fileName: val.fileName,
      order: val.order
    }));

    productAsets.sort((a, b) => {
      let x = a.order;
      let y = b.order;

      if (x > y) {
        return 1;
      }
      if (x < y) {
        return -1;
      }
      return 0;
    });

    const finalList = [];
    for (let order = 1; order <= 4; order++) {
      const product = productAsets.find(item => item.order === order);
      if (product) {
        finalList.push(product);
      } else {
        finalList.push({
          id: '',
          file: undefined,
          path: '',
          fileName: '',
          order: order
        });
      }
    }

    return finalList;
  };

  const getResetProductSection = (formValues: FieldValues) => {
    return companyDetails
      ? {
          ...formValues,
          products: companyDetails.product_details
            ? companyDetails.product_details?.map(item => ({
                productId: item.id ?? '',
                productName: item.name,
                productStatus: item.status,
                productDescription: item.description,
                iosAppLink: item.ios_app_url,
                webAppLink: item.web_app_url,
                androidAppLink: item.android_app_url,
                appScreenshots: item.assets?.filter(
                  asset => asset.source === CompanyProductType.APP
                ).length
                  ? getProductAssets(
                      item.assets.filter(
                        asset => asset.source === CompanyProductType.APP
                      )
                    )
                  : [
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' }
                    ],

                webScreenshots: item.assets?.filter(
                  asset => asset.source === CompanyProductType.WEB
                ).length
                  ? getProductAssets(
                      item.assets.filter(
                        asset => asset.source === CompanyProductType.WEB
                      )
                    )
                  : [
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' },
                      { id: '', file: undefined, path: '', fileName: '' }
                    ]
              }))
            : CreateProductFormDefaultFieldValues.products
        }
      : {};
  };

  const getResetCompetitors = (formValues: FieldValues) => {
    return companyDetails
      ? {
          ...formValues,
          competitors: companyDetails.competitors?.map(item => ({
            id: item.id,
            companyName: item.name,
            companyLogo: {
              file: item.logo_path
                ? new File([], getFileNameFromUrl(item.logo_path))
                : undefined,
              path: item.logo_path
            }
          }))
        }
      : {};
  };

  const getResetTermsSection = (formValues: FieldValues) => {
    return companyDetails
      ? {
          ...formValues,
          terms:
            companyDetails.terms?.map(term => ({
              id: term.id,
              term_key: term.term_key,
              term_value: term.term_value,
              sequence: String(term.sequence)
            })) ?? []
        }
      : {};
  };

  const getResetFunction = (editMode?: SectionMapper) =>
    editMode
      ? {
          [SectionMapper.legalInfo]: getResetLegalInfoSection,
          [SectionMapper.address]: getResetAddressSection,
          [SectionMapper.brand]: getResetBrandSection,
          [SectionMapper.product]: getResetProductSection,
          [SectionMapper.competitors]: getResetCompetitors,
          [SectionMapper.preIpoDetails]: getResetTermsSection
        }[editMode]
      : () => () => ({});

  useEffect(() => {
    async function fetchData() {
      if (companyId) {
        try {
          await getCompany({
            companyId: companyId
          });
        } catch (err) {
          showGenericErrorToast(err);
        }
      }
    }
    fetchData();
  }, [companyId]);

  return {
    companyId: companyId || '',
    companyDetails,
    isLoading,
    countriesList,
    currencyList,
    timeZoneList,
    getResetLegalInfoSection,
    getResetAddressSection,
    getResetBrandSection,
    getResetFunction,
    getResetProductSection,
    sectors,
    companyStatus: companyDetails?.status,
    companyConfiguration,
    getResetCompetitors,
    opportunityList,
    companyName:
      companyDetails?.company_known_name || companyDetails?.registered_name
  };
};

export default useCreateCompany;
